import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { forceFileDownload } from '@/modules/common/helpers/force-download';

import {
  NavBar,
  LoaderIndicator,
  FilterSelect,
  ButtonSecondary,
  HelperViewContextual,
  HeadingDisplay,
  BaseText,
  Row,
  SpinnerLoader,
  BaseIcon
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { Accordion } from '@/modules/common/components/Accordion';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { EFilterTypes } from './types';

import { trackDailyNoteDownload, trackYearlyNoteDownload } from './trackers';

import {
  getTradingNotes,
  getFixedIncomeReport,
  ITradingNoteYear,
  ITradingNoteReport
} from '@/modules/investments/services/investments';

import {
  getDailyBrokerageNote,
  getYearlyBrokerageNote
} from '@/modules/investments/services/trade';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradingNotes',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const filterValue = ref('fixed-income');
const filterOptions = ref([
  { label: 'Renda Fixa', value: EFilterTypes.FIXED_INCOME },
  { label: 'Renda Variável', value: EFilterTypes.VARIABLE_INCOME }
]);

const hasError = ref(false);
const isLoading = ref(false);

const isFetchingYearlyNote = ref<Record<number, boolean>>({});
const openSectionsMap = ref<Record<number, boolean>>({});
const availableYears = ref<ITradingNoteYear[]>([]);

const helperViewProps = computed(() => {
  const errorProps = {
    'generic-error': true,
    buttonLabel: 'Tentar novamente'
  };

  const emptyProps = {
    icon: 'ED0030',
    description: 'Você não possui notas de negociação',
    context: 'neutral'
  };

  return hasError.value ? errorProps : emptyProps;
});

const isEmpty = computed(() => {
  return Boolean(availableYears.value?.length === 0);
});

watch(
  () => availableYears.value,
  sections => {
    const newIsFetchingYearlyNote: Record<number, boolean> = {};
    const newOpenSectionsMap: Record<number, boolean> = {};

    sections?.forEach(section => {
      newIsFetchingYearlyNote[Number(section.year)] = false;
      section.months.forEach(month => {
        newOpenSectionsMap[month.monthIndex] = false;
      });
    });

    openSectionsMap.value = newOpenSectionsMap;
    isFetchingYearlyNote.value = newIsFetchingYearlyNote;
  },
  { immediate: true }
);

function handleToggleAccordion(isOpen: boolean, index: number) {
  openSectionsMap.value[index] = isOpen;
}

function onBack() {
  const cameFrom = (route.query?.module as string) || 'home';

  router.push({ name: cameFrom });
}

async function fetchTradingNotes() {
  try {
    hasError.value = false;
    isLoading.value = true;

    const data = await getTradingNotes(filterValue.value);

    availableYears.value = data.availableDates;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function onClickDownload(
  yearIndex: number,
  monthIndex: number,
  reportIndex: number,
  report: ITradingNoteReport
) {
  try {
    toggleItemLoading(yearIndex, monthIndex, reportIndex);

    if (filterValue.value === EFilterTypes.FIXED_INCOME) {
      const data = await getFixedIncomeReport(report.id, report.name);

      notify({
        group: 'wrn-notifications',
        text: 'Download iniciado',
        type: NotificationTypes.success
      });

      forceFileDownload({
        data,
        type: 'application/pdf',
        fileName: `${report.title}-${report.name}.pdf`
      });
    } else {
      const { generatedURL: url } = await getDailyBrokerageNote(report.id);
      trackDailyNoteDownload(report.title);

      window.open(url, '_blank');
    }
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    toggleItemLoading(yearIndex, monthIndex, reportIndex);
  }
}

async function requestYearlyNote(details: ITradingNoteYear) {
  try {
    isFetchingYearlyNote.value[Number(details.year)] = true;

    const { url } = await getYearlyBrokerageNote(details.year);

    trackYearlyNoteDownload(details.year);

    window.open(url, '_blank');
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    isFetchingYearlyNote.value[Number(details.year)] = false;
  }
}

function toggleItemLoading(
  yearIndex: number,
  monthIndex: number,
  reportIndex: number
) {
  availableYears.value[yearIndex].months[monthIndex].reports[
    reportIndex
  ].isLoading =
    !availableYears.value[yearIndex].months[monthIndex].reports[reportIndex]
      .isLoading;
}

onMounted(() => {
  const cameFrom = route.query?.module as string;

  if (cameFrom === 'trade') filterValue.value = EFilterTypes.VARIABLE_INCOME;

  fetchTradingNotes();
});

return { __sfc: true,router, route, filterValue, filterOptions, hasError, isLoading, isFetchingYearlyNote, openSectionsMap, availableYears, helperViewProps, isEmpty, handleToggleAccordion, onBack, fetchTradingNotes, onClickDownload, requestYearlyNote, toggleItemLoading, NavBar, LoaderIndicator, FilterSelect, ButtonSecondary, HelperViewContextual, HeadingDisplay, BaseText, Row, SpinnerLoader, BaseIcon, BackButton, Accordion, NebraskaColors, EFilterTypes }
}

})